import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import type { KompositionsDetails } from "../lib/komposition";
import type { Pkg, PaymentTermsFields, PaymentTermsResponse } from "../pages/builder";
import { istVollanzahlung, RESTZAHLUNG_STANDARD_TAGE_VOR } from "@maestro/pricing";
import { centsToEuros, parseGermanDecimal, parseGermanEurosToCents } from "./format";
import { Card, ErrorNote, Icon, Input, Segmente, Select, StatusPill, Toggle, DateInput } from "./components";
import { A_LA_CARTE_KONDITIONEN_ENTFAELLT } from "../lib/a-la-carte";
import { OffenePunkteKarte } from "./OffenePunkteKarte";
import { StornoBedingungenFeld, stornoVorlagenListe } from "./StornoVorlagen";
import { PaketUebernehmenPanel } from "./PaketUebernehmenPanel";
import type { InquiryTranslation } from "./auftrag/AuftragGespraechKarte";
import { LANG_LABEL } from "./translation-review";
import { angebotsseitePfad } from "../lib/pfade";
import { useApi } from "../hooks/useApi";

// Antoine, 10.09.2026: "Bei den Zahlungskonditionen soll nicht Standard stehen, sondern
// stattdessen was gewaehlt wurde." Die vier Selects unten zeigten als Leer-Option immer den
// generischen Text "Wie Mandanten-Standard", obwohl der tatsaechlich geltende Wert (Override >
// Mandanten-Default > Software-Fallback) schon geladen ist (`paymentTermsData.effective`, s.
// apps/api/src/lib/payment-terms.ts). Labels hier lokal gehalten (keine Paketgrenze zu apps/api) -
// decken sich mit den Options-Texten weiter unten, duenne Kopie, kein neues Konzept.
const DEPOSIT_METHOD_LABELS: Record<string, string> = {
  none: "Keine",
  stripe_immediate: "Stripe sofort",
  onsite: "Vor Ort",
  invoice_advance: "Rechnung vorab",
};
const FINAL_METHOD_LABELS: Record<string, string> = {
  stripe_advance: "Stripe vorab",
  onsite: "Vor Ort beim Event",
  invoice_before_event: "Rechnung vor Event",
  invoice_after_event: "Rechnung nach Event",
};
const ACCEPTANCE_FLOW_LABELS: Record<string, string> = {
  explicit: "Angebot annehmen (wie bisher)",
  direct_payment: "Direkt zum Zahlungsschritt",
};

/** "Wie Mandanten-Standard" + der real geltende Klartext, falls einer ermittelbar ist - sonst
 *  bleibt es beim reinen Platzhaltertext (kein Raten bei mehrdeutigem/fehlendem Fallback, z.B.
 *  finalPaymentMethod ohne gesetzten Default haengt am alten zweiwertigen finalPaymentMode). */
/**
 * Migration 718 (Antoine 13.10.2026: „Toggle: Tage ab heute oder Tage vor Veranstaltung"): worauf
 * sich die Anzahlungs-Frist bezieht. Dieselben zwei Werte in beiden Masken (Standard + Angebot).
 */
export const ANZAHLUNG_FRIST_MODI = [
  { key: "nach_bestaetigung", label: "nach Auftragsbestätigung" },
  { key: "vor_veranstaltung", label: "vor der Veranstaltung" },
] as const;
export type AnzahlungFristModus = (typeof ANZAHLUNG_FRIST_MODI)[number]["key"];

function standardLabel(effectiveValue: string | null | undefined, labels: Record<string, string>): string {
  const klartext = effectiveValue ? labels[effectiveValue] : undefined;
  return klartext ? `Wie Mandanten-Standard (${klartext})` : "Wie Mandanten-Standard";
}

/**
 * Sentinel fuer "kein Override, Mandanten-Standard nutzen" bei den vier Zahlungs-Konditionen-
 * Selects unten — Radix `Select.Item` (anders als ein natives `<option value="">`) erlaubt keinen
 * leeren `value`-String (design-komponenten-Migration P1, agent-cards+NachschlagenTabs). Muster:
 * `INDUSTRY_KEINE_ANGABE` in `pages/admin/tenant-detail/EinstellungenTab.tsx`.
 */
const MANDANTEN_STANDARD = "__mandanten_standard__";

/**
 * WARTESCHLANGE Punkt 45 — WAS BEIM UMLEGEN DES ANZAHLUNGS-SCHALTERS GESPEICHERT WIRD.
 *
 * ## Warum es diesen Schalter gibt
 *
 * „Keine" stand bisher als vierter Wert in der Auswahl „Anzahlungsart" — also als WEG, obwohl es
 * das Gegenteil eines Weges ist. Genau daraus entstand der Ausfall vom 17.09.2026: am Vorgang
 * Kokorov stand „Anzahlung 100 %" neben „Anzahlungsart: Keine", der Gast nahm an, und es kam keine
 * Zahlungsseite. Ein Betrag ohne Weg war speicherbar.
 *
 * Jetzt entscheidet ein Schalter, OB es eine Anzahlung gibt; die Auswahl darunter entscheidet nur
 * noch, WIE sie bezahlt wird. Die widersprüchliche Kombination lässt sich nicht mehr klicken.
 *
 * ## Warum „aus" ausdrücklich `none` schreibt und nicht `null`
 *
 * `null` heißt in dieser Maske „wie Mandanten-Standard" — und der Standard KANN eine Anzahlung
 * vorsehen. Wer den Schalter ausschaltet, meint aber „für dieses Angebot keine", nicht „nimm was
 * der Betrieb sonst macht". Ein `null` wäre hier also das Gegenteil dessen, was geklickt wurde.
 */
export function anzahlungSchalten(
  an: boolean,
  aktuellerWeg: string | null | undefined,
  verfuegbareWege: string[],
): Partial<PaymentTermsFields> {
  if (!an) {
    // Betrag UND Frist mit abräumen: eine Anzahlungsfrist ohne Anzahlung ist eine Frist auf nichts,
    // und ein stehengebliebener Betrag wäre genau der Zustand, der den Ausfall verursacht hat.
    // `paymentPathway` bleibt bewusst unberuehrt (Antoine, 12.10.2026): auch ohne Anzahlung muss
    // „Kostenuebernahme" als Annahmeweg moeglich sein — der Vertrag sichert dann den vollen Betrag
    // ab (`alsZahlungsvereinbarung`, Zweig „Kostenuebernahme ohne eigenen Betrag"). Die frueher
    // hier stehende Ruecknahme auf „stripe" (Punkt 116) loeschte eine gewaehlte Kostenuebernahme
    // still; seither zeigt der eigene Schalter „Kostenübernahme statt Zahlung" sie sichtbar an.
    return { depositPercentBp: null, depositAmountCents: null, depositDueDays: null, depositDueMode: null, depositMethod: "none" };
  }
  // Einschalten braucht einen Weg. Der bisherige gilt weiter, sofern es ein echter ist; sonst der
  // erste, den dieser Betrieb tatsächlich anbieten kann — nie ein Weg, der später scheitert.
  const behalten = aktuellerWeg && aktuellerWeg !== "none" && verfuegbareWege.includes(aktuellerWeg);
  const weg = behalten ? aktuellerWeg : (verfuegbareWege[0] ?? null);
  return { depositMethod: weg as PaymentTermsFields["depositMethod"] };
}

/**
 * Steht der Anzahlungs-Schalter auf an?
 *
 * Drei Fälle, und der dritte ist der, den man leicht übersieht: ist am Angebot NICHTS gesetzt,
 * gilt der Mandanten-Standard — und der kann sehr wohl eine Anzahlung vorsehen. Der Schalter muss
 * dann an stehen, sonst zeigte die Maske „keine Anzahlung", während das versendete Angebot eine
 * verlangt.
 */
export function istAnzahlungAn(ueberschrieben: string | null | undefined, standard: string | null | undefined): boolean {
  if (ueberschrieben === "none") return false;
  if (ueberschrieben) return true;
  return (standard ?? "stripe_immediate") !== "none";
}

/**
 * Die Anzahlungs-Wege in der Reihenfolge, in der sie angeboten werden — und ob der Betrieb sie hat.
 *
 * `null` bei `wege` heißt „noch nicht geladen": dann bleibt alles wählbar, statt beim Zeichnen
 * kurz alles zu sperren. Dieselbe Überlegung wie bei der Stripe-Kachel (Punkt 46b).
 */
export function anzahlungsWege(wege: Zahlungswege | null): { value: string; label: string; verfuegbar: boolean }[] {
  const frei = (k: keyof Zahlungswege): boolean => wege === null || wege[k];
  return [
    { value: "stripe_immediate", label: "Stripe", verfuegbar: frei("stripe") },
    { value: "invoice_advance", label: "Überweisung", verfuegbar: frei("bankverbindung") },
    { value: "onsite", label: "Vor Ort", verfuegbar: true },
    // WARTESCHLANGE Punkt 45 / K2: die Kostenuebernahme steht jetzt HIER, bei den Wegen der
    // Anzahlung — nicht mehr als eigenes Feld „Zahlungsweg nach der Annahme". Antoine, 18.09.2026:
    // „Kostenuebernahme ist ein Vertrag und ersetzt die Anzahlung. Die Anzahlung ist eine
    // Sicherheitsleistung, wie ein Pfand, der von der Kostenuebernahme ersetzt wird." Ein Weg wie
    // die anderen also — der Gast unterschreibt, statt zu ueberweisen.
    { value: "kostenuebernahme", label: "Kostenübernahme (Vertrag statt Vorauszahlung)", verfuegbar: frei("kostenuebernahme") },
  ];
}

/**
 * Was beim Wechsel des Anzahlungs-Weges gespeichert wird.
 *
 * Der Altbestand trennt die Kostenuebernahme noch in ein eigenes Feld (`paymentPathway`) von den
 * uebrigen Wegen (`depositMethod`). In der Maske ist sie einer von vieren — diese Funktion haelt
 * beide Felder zusammen, damit nie eines von beiden stehenbleibt: eine Kostenuebernahme, die noch
 * auf „Stripe" steht, oder ein Stripe-Weg mit gesetztem Kostenuebernahme-Kennzeichen waere genau
 * die Art Widerspruch, aus der der Ausfall vom 17.09.2026 entstand.
 */
export function anzahlungsWegFelder(wert: string): Partial<PaymentTermsFields> {
  if (wert === "kostenuebernahme") return { paymentPathway: "kostenuebernahme" };
  return {
    depositMethod: (wert === MANDANTEN_STANDARD ? null : wert) as PaymentTermsFields["depositMethod"],
    // Ausdruecklich zurueck auf „stripe": ein zurueckgebliebenes Kostenuebernahme-Kennzeichen
    // wuerde den Zahlungslink weiterhin unterdruecken, obwohl ein anderer Weg gewaehlt wurde.
    paymentPathway: "stripe",
  };
}

/** Welcher Weg in der Auswahl steht — die Kostenuebernahme gewinnt, sie ersetzt die Zahlung. */
export function anzahlungsWegWert(ueberschrieben: PaymentTermsFields, standardWeg: string | null | undefined): string {
  /*
   * DER STANDARD IST KEIN EIGENER WERT, er ist einfach schon eingetragen.
   *
   * Antoine am 18.09.2026: „Standard muss kein eigenes Feld sein. Standard ist einfach ausgefüllt."
   * Vorher stand hier ein Eintrag „Wie Mandanten-Standard (Stripe sofort)" — ein Wert, der etwas
   * ÜBER einen Wert aussagt statt der Wert zu sein. Wer ihn las, musste zweimal denken: erst „was
   * ist der Standard", dann „gilt er hier". Jetzt steht schlicht das, was gilt.
   *
   * „Kein Weg hinterlegt" heißt Stripe — dieselbe Lesart wie überall sonst (`computeDepositCents`
   * und die Übersetzung der Bestandsdaten, Zweig „ungesetzt = Bestandsverhalten").
   */
  if (ueberschrieben.paymentPathway === "kostenuebernahme") return "kostenuebernahme";
  if (ueberschrieben.depositMethod && ueberschrieben.depositMethod !== "none") return ueberschrieben.depositMethod;
  if (standardWeg === "kostenuebernahme") return "kostenuebernahme";
  return standardWeg && standardWeg !== "none" ? standardWeg : "stripe_immediate";
}

/**
 * WARTESCHLANGE Punkt 45 — WEG UND FRIST DER RESTZAHLUNG SIND ZWEI DINGE.
 *
 * ## Das Problem mit dem alten Feld
 *
 * `finalPaymentMethod` vermischt beides: „Rechnung vor Event" sagt zugleich, WOMIT bezahlt wird
 * (Überweisung) und WANN (vor dem Termin). Wer die Frist ändern wollte, musste den Weg anfassen.
 * Genau solche Doppelbedeutungen haben den Ausfall vom 17.09.2026 erzeugt — dort war es „Keine",
 * das gleichzeitig „kein Betrag" und „kein Weg" hieß.
 *
 * In der Maske stehen deshalb zwei Felder. Gespeichert wird weiterhin im Altbestand, solange das
 * Datenmodell nicht umgezogen ist; diese beiden Funktionen sind die Übersetzung in beide
 * Richtungen — an EINER Stelle, nicht verstreut im JSX.
 *
 * ## Die eine Einschränkung, die daraus folgt
 *
 * „Nach der Veranstaltung" kennt der Altbestand nur als `invoice_after_event`, also nur mit
 * Überweisung. Bei Stripe und vor Ort bleibt die Auswahl deshalb vorerst bei „vor dem Event" und
 * „am Veranstaltungstag". Das ist ehrlich eingeschränkt statt frei erfunden — eine Kodierung, die
 * das Feld nicht hergibt, wäre die nächste Doppelbedeutung.
 *
 * Die ANZAHL Tage für „danach" steht seit Punkt 116 (Migration 518) in einem EIGENEN Feld,
 * `finalPaymentDueDaysAfterEvent`. Vorher lag sie in `paymentTermDays` — das ist aber zugleich
 * das Zahlungsziel „N Tage nach Rechnungserhalt" auf der LexOffice-Rechnung, und eine Zahl mit
 * zwei Bedeutungen ist genau die Sorte Doppelung, gegen die dieser Umbau geschrieben ist.
 * `paymentTermDays` wird hier deshalb gar nicht mehr angefasst.
 */
export type RestFristArt = "vor_event" | "am_event_tag" | "nach_event";

export function restzahlungFelder(weg: string, fristArt: RestFristArt, tage: number | null): Partial<PaymentTermsFields> {
  if (weg === "ueberweisung" && fristArt === "nach_event") {
    return {
      finalPaymentMethod: "invoice_after_event",
      finalPaymentDueDaysBeforeEvent: null,
      finalPaymentDueDaysAfterEvent: tage,
    };
  }
  const methode =
    weg === "stripe" ? "stripe_advance" : weg === "onsite" ? "onsite" : "invoice_before_event";
  return {
    finalPaymentMethod: methode as PaymentTermsFields["finalPaymentMethod"],
    // „Am Veranstaltungstag" sind null Tage davor. Bewusst 0 und nicht `null`: `null` hiesse
    // „wie Mandanten-Standard", und der kann eine Frist vorsehen — das waere das Gegenteil dessen,
    // was gewaehlt wurde (dieselbe Ueberlegung wie beim Anzahlungs-Schalter).
    finalPaymentDueDaysBeforeEvent: fristArt === "vor_event" ? tage : 0,
    finalPaymentDueDaysAfterEvent: null,
  };
}

/** Der Rückweg: aus dem Gespeicherten die zwei Felder der Maske. */
/**
 * DER STANDARD STEHT IM FELD, NICHT DANEBEN.
 *
 * Antoine, 18.09.2026: „Standard Sätze müssen vorausgefüllt sein, nicht ausgegraut." Erst für die
 * Stornobedingungen gesagt, dann — zu Recht — für alle: „20 (Standard)" und „1 (Standard)" standen
 * weiter als grauer Platzhalter da. Ein Platzhalter sieht aus wie ein Wert, ist aber keiner: man
 * kann ihn nicht anpassen, ohne ihn erst abzutippen, und man sieht nicht, ob er wirklich gilt.
 *
 * GESPEICHERT WIRD TROTZDEM NUR EINE ECHTE ABWEICHUNG. Stünde der Standardwert nach dem ersten
 * Speichern als eigener Wert am Angebot, trüge jedes Angebot eine Kopie — und eine spätere
 * Änderung am Betriebs-Standard erreichte die laufenden Angebote nicht mehr. Wer den Wert wieder
 * auf den Standard bringt oder das Feld leert, löst die Abweichung auf.
 */
export function standardOderAbweichung(eingabe: string | null, standard: number | null | undefined): { wert: number | null; istAbweichung: boolean } | "ungueltig" {
  const roh = (eingabe ?? "").trim();
  if (roh === "") return { wert: null, istAbweichung: false };
  const n = Number(roh.replace(",", "."));
  if (!Number.isFinite(n) || n < 0) return "ungueltig";
  return { wert: n, istAbweichung: standard == null || n !== standard };
}

export function restzahlungAus(
  ueberschrieben: PaymentTermsFields,
  standard: PaymentTermsFields | null | undefined,
): { weg: string; fristArt: RestFristArt; tage: number | null } {
  /*
   * DIE MASKE ZEIGT, WAS GILT — nicht nur, was abweicht.
   *
   * Antoine am 18.09.2026 an der laufenden Oberfläche: oben stand „Am Veranstaltungstag", unten im
   * Zusammenfassungssatz „5 Tage vor der Veranstaltung fällig". Beides zugleich, beides falsch
   * nebeneinander.
   *
   * Ursache: hier wurde die Tageszahl NUR aus der Abweichung gelesen, während der Satz darunter
   * serverseitig aus dem zusammengeführten Stand kommt (Betriebs-Standard plus Abweichung). Stand
   * im Betriebs-Standard eine Frist und am Angebot keine, behaupteten Feld und Satz Verschiedenes.
   * Zwei Wahrheiten über dieselbe Frist — genau der Fehler, gegen den dieser ganze Umbau
   * geschrieben ist, diesmal von mir selbst gebaut.
   */
  const methode = ueberschrieben.finalPaymentMethod ?? standard?.finalPaymentMethod ?? null;
  if (methode === "invoice_after_event") {
    return { weg: "ueberweisung", fristArt: "nach_event", tage: ueberschrieben.finalPaymentDueDaysAfterEvent ?? standard?.finalPaymentDueDaysAfterEvent ?? null };
  }
  const weg = methode === "stripe_advance" ? "stripe" : methode === "onsite" ? "onsite" : "ueberweisung";
  // Punkt 116: ist nirgends eine Frist hinterlegt, gilt die Vorgabe (14 Tage vorher) — und genau
  // die zeigt die Maske, statt „Am Veranstaltungstag" zu behaupten.
  const tage = ueberschrieben.finalPaymentDueDaysBeforeEvent ?? standard?.finalPaymentDueDaysBeforeEvent ?? RESTZAHLUNG_STANDARD_TAGE_VOR;
  // Null Tage davor IST der Veranstaltungstag — sonst stuende dort „0 Tage vor dem Event", was
  // niemand so sagt.
  return { weg, fristArt: tage != null && tage > 0 ? "vor_event" : "am_event_tag", tage };
}

/**
 * WARTESCHLANGE Punkt 45 — WANN DER RESTZAHLUNGSBLOCK GANZ VERSCHWINDET.
 *
 * Deckt die Anzahlung bereits den vollen Betrag, bleibt nichts übrig. Dann gehört der Block nicht
 * ausgegraut, sondern **weg**: ein Feld, das nichts bewirkt, ist eine Falle — man füllt es aus und
 * glaubt, etwas geregelt zu haben. Red Team (2) im Konzept nennt genau das: zwei Zahlungen über
 * denselben Betrag.
 *
 * ## Die Ausnahme, die Antoine geklärt hat
 *
 * Bei Kostenübernahme verschwindet er NICHT. Antoine, 18.09.2026: „Kostenübernahme ist ein Vertrag
 * und ersetzt die Anzahlung. Die Anzahlung ist eine Sicherheitsleistung, wie ein Pfand, der von
 * der Kostenübernahme ersetzt wird."
 *
 * Es ist also nichts vorausbezahlt — die volle Summe bleibt offen. Eine frühere Fassung im
 * geteilten Paket hat die Restzahlung hier verschwinden lassen; beim Vorgang Clara Emmerich wären
 * damit 20.170,50 € aus der Forderung gefallen, ohne dass ein Cent geflossen wäre.
 *
 * `gesamtCents` darf `null` sein (die Summe steht im Builder nicht immer bereit) — dann zählt nur
 * der Prozentsatz. Ein fester Betrag ohne bekannte Summe lässt sich nicht vergleichen, und geraten
 * wird hier nichts.
 */
export function restzahlungEntfaellt(
  prozentBp: number | null | undefined,
  betragCents: number | null | undefined,
  gesamtCents: number | null | undefined,
  kostenuebernahme: boolean,
): boolean {
  /*
   * KEINE eigene Rechnung. `istVollanzahlung` (@maestro/pricing) entscheidet dasselbe beim
   * Speichern auf dem Server — stünde die Regel hier ein zweites Mal, gäbe es zwei Wahrheiten
   * darüber, ob noch etwas offen ist. Genau das ist der Fehler, den Punkt 45 abschafft.
   *
   * Am 18.09.2026 hätte es fast gereicht: diese Kopie prüfte den Prozentsatz ZUERST, der Server
   * (wie seit jeher `computeDepositCents`) den festen Betrag. Bei „30 % Standard, aber 12.000 €
   * fest für dieses Angebot" hätte die Maske eine Restzahlung angezeigt, die es nicht gibt —
   * oder umgekehrt.
   */
  return istVollanzahlung(
    { prozentBp: prozentBp ?? null, betragCents: betragCents ?? null, kanal: kostenuebernahme ? "kostenuebernahme" : "stripe", frist: { art: "sofort" } },
    gesamtCents ?? null,
  );
}

/** Die Wege der Restzahlung — wie bei der Anzahlung, aber OHNE Kostenübernahme (K2). */
export function restzahlungsWege(wege: Zahlungswege | null): { value: string; label: string; verfuegbar: boolean }[] {
  const frei = (k: keyof Zahlungswege): boolean => wege === null || wege[k];
  return [
    { value: "stripe", label: "Stripe", verfuegbar: frei("stripe") },
    { value: "ueberweisung", label: "Überweisung", verfuegbar: frei("bankverbindung") },
    { value: "onsite", label: "Vor Ort", verfuegbar: true },
  ];
}

/**
 * Punkt 58 (Antoine 18.09.2026): „Unterhalb von ‚Kundenseite ansehen‘ soll auch ein Link stehen zu
 * Stripe Zahlung ansehen. Damit sich der Betreiber die Stripe Zahlungsseite ansehen kann." Und:
 * „Hinter dem Link zur Kundenseite und hinter dem Stripe Link soll es auch ein Copy Zeichen geben,
 * um die jeweilige URL in den Zwischenspeicher zu kopieren."
 *
 * Ein Knopf, der eine Adresse in die Zwischenablage legt und für zwei Sekunden bestätigt. Das
 * `navigator.clipboard` fehlt in älteren und in unsicheren Kontexten (kein HTTPS) — dann bleibt es
 * beim Versuch, ohne Absturz und ohne falsche Erfolgsmeldung.
 */
function KopierKnopf({ wert, titel }: { wert: string; titel: string }) {
  const [kopiert, setKopiert] = useState(false);
  return (
    <button
      type="button"
      title={titel}
      aria-label={titel}
      className="shrink-0 rounded p-0.5 text-secondary-fixed-dim hover:text-primary"
      onClick={() => {
        void (async () => {
          try {
            await navigator.clipboard.writeText(wert);
            setKopiert(true);
            setTimeout(() => setKopiert(false), 2000);
          } catch {
            // Bewusst still: eine Fehlermeldung über eine fehlgeschlagene Zwischenablage hilft
            // niemandem — die Adresse steht daneben und lässt sich von Hand markieren.
          }
        })();
      }}
    >
      <Icon name={kopiert ? "check" : "content_copy"} className="text-[14px]" />
    </button>
  );
}

/** Eine Zahlungszeile, soweit diese Karte sie braucht (GET /api/events/:id/payments). */
interface ZahlungsZeile {
  id: string;
  status: string;
  paymentType: string;
  checkoutUrl: string | null;
}

/** Antwort von GET /api/settings/zahlungswege — was dieser Betrieb anbieten kann. */
export interface Zahlungswege {
  stripe: boolean;
  bankverbindung: boolean;
  kostenuebernahme: boolean;
}

/**
 * "Nachschlagen, nicht Lesen" — Region 4b. Der Ein-Reiter-Umschalter selbst („immer nur EINER
 * offen", Antoines Layout-Regel vom 03.08.2026) plus die beiden reinen Lese-Tabs „Anfrage im
 * Original"/„Wie MAESTRO die Anfrage gelesen hat". Rendert `OffenePunkteKarte` und
 * `PaketUebernehmenPanel` als Kinder fuer die uebrigen Tabs. Aus
 * `builder.tsx` (Schritt 10, Einheit D, Konzept § 2.2/§ 4.10).
 *
 * A3 (UX-Polish 24.08.2026): der fruehere fuenfte Reiter „Variante bearbeiten" ist hier
 * ersatzlos raus — sein Inhalt ist vollstaendig umgezogen (Titel in die Varianten-Kachel,
 * Gaestezahl + Tagesplanung in den Eckdaten-Drawer des Seitenkopfs, s. Kommentar an `reiter`
 * unten). Damit sind `days`/`activeDay`/`option` und ihre Handler keine Props dieser Komponente
 * mehr; der frueher hier stehende Punkt 2 der Begruendung ist gegenstandslos geworden.
 *
 * Reiner Ortswechsel (Phase A), KEIN Phase B — zwei unabhaengige Gruende, beide im echten Code
 * verifiziert vor dieser Extraktion:
 *
 * 1. `offenerReiter` sieht auf den ersten Blick wie reiner lokaler UI-State dieser Karte aus
 *    (Muster von `applyingPkgId`, Schritt 6). Er wird aber NICHT nur hier geschrieben: der
 *    Parent-Effekt `beimErstenMalGeoeffnet` (builder.tsx) oeffnet den "offen"-Reiter automatisch
 *    beim ersten Laden, wenn `komposition.abdeckung.unmatchedRequests` nicht leer ist — eine
 *    externe Schreibquelle ausserhalb dieser Komponente. `offenerReiter`/`setOffenerReiter`
 *    bleiben deshalb Props vom Parent, nicht lokaler State.
 * 2. **Abweichung vom Konzept, im echten Code gefunden:** Neben den drei im Konzept § 2.2
 *    genannten Kind-Komponenten enthaelt derselbe Ein-Reiter-Umschalter inzwischen einen
 *    VIERTEN Tab „Zahlungs-Konditionen" (Kommentar im Original: „Punkt 4, Session 20.08.2026")
 *    — nach dem Analyse-Stand des Konzepts gebaut, dort nirgends erwaehnt (§ 1.2/§ 2 listen nur
 *    Anfrage/Verstaendnis/Offen/Pakete/Variante). Er ist strukturell Teil desselben Schalters
 *    (derselbe `offenerReiter`-Zustand) und wird darum als Teil des Rahmens mitgenommen (reines
 *    JSX, keine neue Logik) statt als fuenfte, in diesem Schritt nicht benannte Komponente
 *    abgespalten zu werden — sein State (`ptOverride`/`ptLoadedFor`/`ptSaving`/`ptError`/
 *    `savePaymentTerms`/die `paymentTermsQ`-Query) bleibt unveraendert beim Parent (Props).
 */
export function NachschlagenTabs({
  customerNotes,
  inquiryLanguage,
  inquiryTranslation,
  verstaendnis,
  komposition,
  editable,
  setError,
  packages,
  packagesLoading,
  onApplyPackage,
  onAltesAngebot,
  offenerReiter,
  onSetOffenerReiter,
  eventId,
  offerValidUntil,
  onPatchValidUntil,
  paymentTermsLoading,
  variantenGesamt = 0,
  onOffenenPunktAusblenden,
  paymentTermsData,
  zahlungswege = null,
  ptOverride,
  ptLoadedFor,
  ptSaving,
  ptError,
  onSavePaymentTerms,
  aLaCarte = false,
  andereBepreist = false,
}: {
  /**
   * Migration 731: die gerade bearbeitete Variante ist „À la carte“ (Abrechnung vor Ort) - für sie
   * entfallen die Zahlungs-Konditionen. `andereBepreist`: es gibt daneben bepreiste Varianten, für
   * die sie weiter gelten (die Konditionen gelten fürs ganze Angebot, nicht je Variante).
   */
  aLaCarte?: boolean;
  andereBepreist?: boolean;
  customerNotes: string | null | undefined;
  /** WP6 (Auftrags-Dashboard, Phase C.1): fuer den Umschalter "Original | Deutsch" — siehe
   *  `ui/auftrag/AuftragAnfrageKarte.tsx` (dieselbe Datenquelle, GET /api/events/:id). */
  inquiryLanguage?: string | null;
  inquiryTranslation?: InquiryTranslation | null;
  verstaendnis: string | null | undefined;
  komposition: KompositionsDetails;
  editable: boolean;
  setError: (message: string) => void;
  packages: Pkg[];
  packagesLoading: boolean;
  onApplyPackage: (pkg: Pkg | Pkg[]) => Promise<void>;
  /** „Altes Angebot übernehmen“ (29.09.2026): kein Reiter, sondern ein Fenster — der Chip öffnet es. */
  onAltesAngebot?: () => void;
  offenerReiter: "anfrage" | "verstaendnis" | "offen" | "pakete" | "variante" | "zahlung" | null;
  onSetOffenerReiter: (reiter: "anfrage" | "verstaendnis" | "offen" | "pakete" | "variante" | "zahlung" | null) => void;
  eventId: string | undefined;
  offerValidUntil: string | null | undefined;
  onPatchValidUntil: (value: string) => Promise<void>;
  paymentTermsLoading: boolean;
  /**
   * Punkt 77, Zustand 2 (21.09.2026): wie viele Fassungen das Angebot insgesamt hat. Nur der
   * Builder weiss das; die Abdeckungsliste kennt allein die betroffenen Fassungen. Ohne die Zahl
   * kann die Karte „1 von 3" nicht sagen - und genau so hat Antoine es formuliert.
   */
  variantenGesamt?: number;
  /**
   * Punkt 77, das Wegklicken (21.09.2026): einen offenen Punkt ausblenden oder zurueckholen.
   * Kommt vom Builder, weil nur er den Vorgang kennt und die Liste danach neu laedt.
   */
  onOffenenPunktAusblenden?: (wunsch: string, ausgeblendet: boolean) => void;
  paymentTermsData: PaymentTermsResponse | null | undefined;
  /** Punkt 45: was dieser Betrieb anbieten kann. `null` = noch nicht geladen. */
  zahlungswege?: Zahlungswege | null;
  ptOverride: PaymentTermsFields;
  ptLoadedFor: string | null;
  ptSaving: boolean;
  ptError: string | null;
  onSavePaymentTerms: (patch: Partial<PaymentTermsFields>) => Promise<void>;
}) {
  const hatAnfrage = Boolean(customerNotes && customerNotes.trim().length > 0);
  // WP6: Umschalter "Original | Deutsch" — nur sichtbar, wenn tatsaechlich eine Uebersetzung
  // vorliegt (sofort-beim-Eingang-Automatik oder der manuelle Nachholweg, s. AuftragAnfrageKarte).
  const [anfrageAnsicht, setAnfrageAnsicht] = useState<"original" | "de">("original");
  // WARTESCHLANGE Punkt 45: der Zustand des Anzahlungs-Schalters und die Wege, die dieser Betrieb
  // tatsaechlich anbieten kann. Beides als benannte Funktionen (s. oben), damit die Fallunter-
  // scheidung pruefbar bleibt statt im JSX zu verschwinden.
  const anzahlungAn = istAnzahlungAn(ptOverride.depositMethod, paymentTermsData?.effective?.depositMethod);
  // Ohne Anzahlung steht in beiden Betragsfeldern nichts (Antoine, 13.10.2026: „30 bei keiner
  // Anzahlung"). Die Felder sind ungesteuert — der angezeigte Wert gehoert deshalb in den `key`,
  // sonst bleibt nach dem Schalter oder einem Neuladen der alte Wert im Feld stehen.
  const anzahlungProzent = ptOverride.depositPercentBp ?? paymentTermsData?.effective?.depositPercentBp;
  const anzahlungProzentText = anzahlungAn && anzahlungProzent != null ? (anzahlungProzent / 100).toString() : "";
  const anzahlungFix = ptOverride.depositAmountCents ?? paymentTermsData?.effective?.depositAmountCents;
  const anzahlungFixText = anzahlungAn && anzahlungFix != null ? centsToEuros(anzahlungFix) : "";
  // Die Daten kommen asynchron — ohne eigene Wahl folgt der Umschalter dem gespeicherten Wert.
  const [anzahlungEinheitWahl, setAnzahlungEinheit] = useState<"prozent" | "euro" | null>(null);
  const anzahlungEinheit = anzahlungEinheitWahl ?? (anzahlungFix != null && anzahlungProzent == null ? "euro" : "prozent");
  const rest = restzahlungAus(ptOverride, paymentTermsData?.effective ?? null);
  /*
   * DIE AUSWAHL MUSS STEHENBLEIBEN, AUCH OHNE ZAHL.
   *
   * Antoine, 18.09.2026: „Tage vor Veranstaltung. Wo kann das ausgefüllt werden? […] Es kann aber
   * gar nicht ausgewählt werden."
   *
   * Er hatte recht, und der Grund war ein Konstruktionsfehler von mir: die Frist-Art wurde
   * AUSSCHLIESSLICH aus der Tageszahl abgeleitet. „Tage vor der Veranstaltung" ohne eingetragene
   * Zahl war damit von „am Veranstaltungstag" nicht unterscheidbar — die Auswahl sprang sofort
   * zurück, und das Feld für die Tage erschien nie. Ein Feld, das man nicht erreichen kann, weil
   * man erst hineinschreiben müsste, um es sichtbar zu machen.
   *
   * Die Wahl lebt deshalb kurz in der Maske, bis eine Zahl da ist. `null` heißt „noch nichts
   * gewählt, nimm was in den Daten steht".
   */
  const [fristWahl, setFristWahl] = useState<RestFristArt | null>(null);
  const fristArt: RestFristArt = fristWahl ?? rest.fristArt;
  /*
   * Punkt 58: die Adresse der Stripe-Zahlungsseite, falls es sie schon gibt.
   *
   * Gelesen wird derselbe Endpunkt wie in der Abrechnung — keine zweite Quelle über Geld. Gesucht
   * wird die jüngste OFFENE Zahlung mit hinterlegtem Link: eine bereits abgeschlossene Zahlung
   * führt auf eine Seite, die nichts mehr annimmt, und eine stornierte erst recht nicht.
   */
  const { data: zahlungenData } = useApi<ZahlungsZeile[]>(eventId ? `/api/events/${eventId}/payments` : null);
  const zahlungsLink =
    (Array.isArray(zahlungenData) ? zahlungenData : []).find(
      (z) => (z.status === "pending" || z.status === "processing") && !!z.checkoutUrl,
    )?.checkoutUrl ?? null;
  // Punkt 45: die Angebotssumme steht in dieser Karte nicht bereit — deshalb `null`. Folge: ein
  // FESTER Anzahlungsbetrag in Hoehe der Summe blendet den Block (noch) nicht aus, ein Prozentsatz
  // von 100 schon. Die sichere Richtung: lieber einen Block zeigen, der entfallen koennte, als
  // einen verstecken, der gebraucht wird.
  const restEntfaellt = restzahlungEntfaellt(
    ptOverride.depositPercentBp ?? paymentTermsData?.effective?.depositPercentBp,
    ptOverride.depositAmountCents ?? paymentTermsData?.effective?.depositAmountCents,
    null,
    (ptOverride.paymentPathway ?? paymentTermsData?.effective?.paymentPathway) === "kostenuebernahme",
  ) && anzahlungAn;
  const kostenuebernahmeOhneAnzahlung =
    !anzahlungAn && (ptOverride.paymentPathway ?? paymentTermsData?.effective?.paymentPathway) === "kostenuebernahme";
  const verfuegbareAnzahlungsWege = anzahlungsWege(zahlungswege)
    .filter((w) => w.verfuegbar)
    .map((w) => w.value);
  const hatUebersetzung = Boolean(inquiryTranslation?.textDe);
  const anfrageSprache = inquiryTranslation?.lang && inquiryTranslation.lang !== "de" ? inquiryTranslation.lang : inquiryLanguage;
  const hatVerstaendnis = Boolean(verstaendnis) || komposition.antwortenAufFragen.length > 0;
  // §5.12: Wuensche der Anfrage ohne Katalog-Entsprechung + verworfene Modell-Zeilen.
  const offeneWuensche = komposition.abdeckung.unmatchedRequests;
  // Punkt 77, Zustand 2: ein Wunsch, der nur in EINIGEN Fassungen steht, ist ebenfalls etwas
  // Offenes - ohne diese Zeile bliebe der Reiter verborgen, obwohl die Karte etwas zu sagen hat.
  const teilweise = komposition.abdeckung.teilweiseBeruecksichtigt;
  // Punkt 77, das Wegklicken: ausgeblendete Punkte zaehlen NICHT mehr als offen - sonst bliebe der
  // Reiter mit einer Zahl stehen, die der Betrieb gerade bewusst abgeraeumt hat.
  const weggeklickt = komposition.abdeckung.weggeklickt;
  const ausgeblendet = new Set(weggeklickt);
  const offeneZahl = offeneWuensche.filter((w) => !ausgeblendet.has(w)).length + teilweise.filter((e) => !ausgeblendet.has(e.wunsch)).length;
  const hatOffenes = offeneZahl > 0 || weggeklickt.length > 0 || komposition.abdeckung.strippedCount > 0;
  const aktivePakete = packages.filter((pk) => pk.archivedAt == null);
  const reiter: { id: typeof offenerReiter; icon: string; label: string; zahl?: number }[] = [
    ...(hatAnfrage ? [{ id: "anfrage" as const, icon: "forum", label: "Anfrage im Original" }] : []),
    ...(hatVerstaendnis ? [{ id: "verstaendnis" as const, icon: "auto_awesome", label: "Wie MAESTRO die Anfrage gelesen hat" }] : []),
    // Die Zahl steht im Chip, damit man EINGEKLAPPT sieht, dass etwas offen ist.
    ...(hatOffenes ? [{ id: "offen" as const, icon: "rule", label: "Aus der Anfrage noch offen", zahl: offeneZahl || undefined }] : []),
    ...(editable ? [{ id: "pakete" as const, icon: "inventory_2", label: "Paket übernehmen", zahl: aktivePakete.length }] : []),
    /* A3 (UX-Polish 24.08.2026): der Reiter „Variante bearbeiten" ist entfallen. Sein Inhalt war
       zweiteilig und ist vollstaendig umgezogen, nichts davon geloescht:
         · Titel                                            -> per Klick direkt in der
           Varianten-Kachel (`VariantenLeiste.tsx`).
         · Datum/Gaestezahl (Veranstaltung + Variante)/Uhrzeit/Adresse/Tagesplanung -> EIN Klick
           auf die Meta-Zeile im Seitenkopf oeffnet das Eckdaten-Fenster
           (`ui/VeranstaltungsDatenDrawer.tsx`, von `builder.tsx` geoeffnet). Ruecksbau 01.09.2026:
           dazwischen kurz einzelne Inline-Chips + ein ausklappbarer Bereich statt eines Fensters
           (`ui/TagesplanungPanel.tsx`, seither wieder umbenannt) — Antoine wollte nach dem
           Ausprobieren wieder EIN Fenster fuer alles.
       Der Grund: dieser Umschalter heisst „Nachschlagen, nicht Lesen" und traegt sonst nur
       Lese-Inhalte. Die Angaben, die man am haeufigsten AENDERT, ausgerechnet dort zu
       verstecken, war der Fehler. */
    // Punkt 4 (20.08.2026): immer sichtbar (auch nach Versand, C-02-Lock zeigt sich
    // erst beim Speichern als Fehlermeldung) - die Zahlungs-Konditionen-Karte
    // braucht keine geladene Options-ID.
    { id: "zahlung" as const, icon: "payments", label: "Zahlungs-Konditionen" },
  ];
  if (reiter.length === 0) return null;
  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {reiter.map((r) => {
          const offen = offenerReiter === r.id;
          return (
            <Fragment key={r.id}>
            <button
              type="button"
              aria-expanded={offen}
              onClick={() => onSetOffenerReiter(offen ? null : r.id)}
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-beleg-mikro transition-colors ${
                offen
                  ? "border-primary/50 bg-primary/[0.07] text-primary"
                  : "border-outline-variant/60 text-on-surface-variant hover:border-primary/40 dark:border-outline/40"
              }`}
            >
              <Icon name={r.icon} className="text-[16px]" />
              {r.label}
              {r.zahl != null && (
                <span className="rounded-full bg-surface-container-high px-1.5 text-beleg-mikro text-on-surface-variant dark:bg-white/[0.08] dark:text-[#b8ae9e]">{r.zahl}</span>
              )}
              <Icon name={offen ? "expand_less" : "expand_more"} className="text-[16px] opacity-60" />
            </button>
            {/* „Altes Angebot übernehmen“ (29.09.2026) direkt neben „Paket übernehmen“ — ein
                Fenster statt eines Reiters, darum ohne Auf-/Zuklapp-Pfeil. */}
            {r.id === "pakete" && onAltesAngebot && (
              <button
                type="button"
                onClick={onAltesAngebot}
                data-testid="chip-altes-angebot"
                className="flex items-center gap-1.5 rounded-full border border-outline-variant/60 px-3 py-1.5 text-beleg-mikro text-on-surface-variant transition-colors hover:border-primary/40 dark:border-outline/40"
              >
                <Icon name="content_copy" className="text-[16px]" />
                Altes Angebot übernehmen
              </button>
            )}
            </Fragment>
          );
        })}
      </div>

      {/* Bewusst ROH und ungekuerzt (§ 5.10 / RC-11): Was der Kunde geschrieben hat,
          darf nicht durch eine Auslegung ersetzt werden. */}
      {offenerReiter === "anfrage" && customerNotes && (
        <Card className="space-y-2 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            {anfrageSprache && anfrageSprache !== "de" && (
              <span className="rounded-full bg-surface-container-high px-2 py-0.5 font-label-md text-[10.5px] font-semibold text-on-surface-variant dark:bg-white/[0.08] dark:text-[#b8ae9e]">
                {LANG_LABEL[anfrageSprache] ?? anfrageSprache.toUpperCase()}
              </span>
            )}
            {/* WP6: nur bei tatsaechlich vorhandener Uebersetzung — der Nachholweg selbst
                ("Jetzt uebersetzen") lebt bewusst NUR auf der Dashboard-Karte, nicht doppelt hier. */}
            {hatUebersetzung && (
              <div className="ml-auto inline-flex items-center gap-1 rounded-full border border-outline-variant/60 p-0.5 dark:border-outline/40" role="tablist" aria-label="Original oder Deutsch anzeigen">
                {(["original", "de"] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    role="tab"
                    aria-selected={anfrageAnsicht === v}
                    data-testid={`anfrage-original-ansicht-${v}`}
                    onClick={() => setAnfrageAnsicht(v)}
                    className={`rounded-full px-3 py-1 font-body-sm text-[11.5px] font-semibold transition-colors ${
                      anfrageAnsicht === v ? "bg-primary/[0.1] text-primary" : "text-on-surface-variant hover:text-on-background dark:hover:text-inverse-on-surface"
                    }`}
                  >
                    {v === "original" ? "Original" : "Deutsch"}
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className="whitespace-pre-wrap text-beleg-rechenweg leading-relaxed text-on-surface dark:text-inverse-on-surface">
            {anfrageAnsicht === "de" && hatUebersetzung ? inquiryTranslation!.textDe : customerNotes}
          </p>
        </Card>
      )}

      {/* K0.6 (Befund F4): Verstaendnis und die Antworten auf die Kundenfragen waren
          frueher gar nicht sichtbar - B4 und B7 des Benchmarks liessen sich am
          Bildschirm nicht beurteilen. Sichtbar bleiben sie; nur eben auf Abruf. */}
      {offenerReiter === "verstaendnis" && (
        <Card className="space-y-3 p-4 text-beleg-rechenweg">
          {verstaendnis && (
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <Icon name="psychology" className="shrink-0 text-[13px] text-primary" />
                <span className="font-medium text-on-background dark:text-inverse-on-surface">So wurde die Anfrage gelesen</span>
              </div>
              <p className="whitespace-pre-line text-on-surface-variant">{verstaendnis}</p>
            </div>
          )}
          {komposition.antwortenAufFragen.length > 0 && (
            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <Icon name="quiz" className="shrink-0 text-[13px] text-primary" />
                <span className="font-medium text-on-background dark:text-inverse-on-surface">Antworten auf die Fragen des Kunden</span>
              </div>
              <ul className="space-y-1.5">
                {komposition.antwortenAufFragen.map((a, i) => (
                  <li key={i} className="text-on-surface-variant">
                    <span className="font-medium text-on-background dark:text-inverse-on-surface">{a.frage}</span>
                    <br />
                    {a.antwort}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}

      {/* §5.12 — "Aus der Anfrage noch offen": der Inhalt steht in
          `ui/OffenePunkteKarte.tsx`, damit er ohne diese 3.000-Zeilen-Seite
          gerendert und geprueft werden kann. */}
      {offenerReiter === "offen" && (
        <OffenePunkteKarte
          unmatchedRequests={offeneWuensche}
          teilweiseBeruecksichtigt={teilweise}
          variantenGesamt={variantenGesamt}
          strippedCount={komposition.abdeckung.strippedCount}
          weggeklickt={weggeklickt}
          onAusblenden={onOffenenPunktAusblenden}
        />
      )}

      {/* Punkt 4 (20.08.2026): Override je Vorgang (nicht je Variante — Zahlungs-
          konditionen gelten fuers ganze Angebot). Bestehende Route, bisher ohne
          Builder-UI (nur der Mandanten-Default hatte ein Formular, s. settings.tsx).
          Placeholder in jedem Feld zeigen den Mandanten-Default/effektiven Wert, ein
          leeres Feld heisst "wie Mandanten-Standard" (kein Override gesetzt). */}
      {offenerReiter === "zahlung" && aLaCarte && (
        <Card className="space-y-2 p-4" data-testid="konditionen-a-la-carte">
          <div className="flex items-center gap-2">
            <StatusPill meta={{ label: A_LA_CARTE_KONDITIONEN_ENTFAELLT, tone: "neutral", icon: "block" }} />
            <span className="text-beleg font-bold text-on-background dark:text-inverse-on-surface">Zahlungs-Konditionen</span>
          </div>
          <p className="text-[12.5px] text-on-surface-variant dark:text-secondary-fixed-dim">
            Diese Variante ist à la carte: keine Anzahlung, kein Zahlungsplan, keine Rechnung aus MAESTRO.
            {andereBepreist ? " Für die bepreisten Varianten dieses Angebots gelten die Konditionen unten weiter." : ""}
          </p>
        </Card>
      )}
      {offenerReiter === "zahlung" && (!aLaCarte || andereBepreist) && (
        <Card className="space-y-4 p-4">
          {paymentTermsLoading && !paymentTermsData ? (
            <p className="font-body-sm text-body-sm text-on-surface-variant">Lädt …</p>
          ) : (
            <>
              {/* WARTESCHLANGE Punkt 45 — der Schalter, der den Ausfall vom 17.09.2026 unmöglich macht.
                  Vorher war „Keine" ein Wert in der Auswahl „Anzahlungsart", also ein WEG. Damit ließ
                  sich „Anzahlung 100 %" neben „Anzahlungsart: Keine" speichern — der Gast nahm an, und
                  es kam keine Zahlungsseite. Jetzt entscheidet der Schalter das OB, die Auswahl nur
                  noch das WIE. */}
              <div className="flex items-center gap-3 rounded-lg bg-surface-variant/40 px-3 py-2.5 dark:bg-surface-dim/40">
                <Toggle
                  checked={anzahlungAn}
                  onChange={(an) => void onSavePaymentTerms(anzahlungSchalten(an, ptOverride.depositMethod, verfuegbareAnzahlungsWege))}
                  label="Anzahlung vereinbaren"
                />
                <span className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                  {anzahlungAn ? "Anzahlung vereinbart" : kostenuebernahmeOhneAnzahlung ? "Keine Anzahlung – der Gast unterschreibt eine Kostenübernahme" : "Keine Anzahlung – der Gast zahlt alles auf einmal"}
                </span>
              </div>
              {/* Antoine, 12.10.2026: auch ohne Anzahlung muss die Kostenübernahme als Annahmeweg
                  wählbar sein. Die Auswahl „Wie wird die Anzahlung bezahlt?" ist dann gesperrt —
                  deshalb ein eigener Schalter, nur solange keine Anzahlung vereinbart ist. */}
              {!anzahlungAn && verfuegbareAnzahlungsWege.includes("kostenuebernahme") && (
                <div className="flex items-center gap-3 rounded-lg bg-surface-variant/40 px-3 py-2.5 dark:bg-surface-dim/40">
                  <Toggle
                    checked={kostenuebernahmeOhneAnzahlung}
                    onChange={(an) => void onSavePaymentTerms({ paymentPathway: an ? "kostenuebernahme" : "stripe" })}
                    label="Kostenübernahme statt Zahlung"
                  />
                  <span className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                    Kostenübernahme statt Zahlung
                  </span>
                </div>
              )}
              <div className={`grid gap-3.5 sm:grid-cols-[auto_1fr] sm:items-end ${anzahlungAn ? "" : "pointer-events-none opacity-50"}`}>
                {/* Antoine, 13.10.2026: „mit toggle entweder % oder €" — ein Feld statt zwei nebeneinander. */}
                <div className="inline-flex rounded-lg border border-outline-variant/60 p-0.5 dark:border-outline/40" role="group" aria-label="Anzahlung als">
                  {(["prozent", "euro"] as const).map((e) => (
                    <button
                      key={e}
                      type="button"
                      aria-pressed={anzahlungEinheit === e}
                      onClick={() => setAnzahlungEinheit(e)}
                      className={`rounded-md px-3 py-2 font-body-sm text-body-sm ${anzahlungEinheit === e ? "bg-primary text-on-primary" : "text-on-surface-variant dark:text-secondary-fixed-dim"}`}
                    >
                      {e === "prozent" ? "%" : "€"}
                    </button>
                  ))}
                </div>
                {anzahlungEinheit === "prozent" ? (
                <label className="block">
                  <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                    Anzahlung in Prozent
                  </span>
                  <Input
                    key={`dp-${ptLoadedFor ?? ""}-${anzahlungProzentText}`}
                    inputMode="decimal"
                    /* Der Standard steht IM Feld, nicht als grauer Platzhalter daneben. */
                    defaultValue={anzahlungProzentText}
                    placeholder="z. B. 30"
                    onBlur={(e) => {
                      const raw = e.target.value.trim();
                      if (raw === "") return void onSavePaymentTerms({ depositPercentBp: null });
                      const pct = parseGermanDecimal(raw);
                      if (pct == null) return setError("Ungültiger Anzahlungs-Prozentsatz.");
                      const bp = Math.round(pct * 100);
                      // Gleich dem Betriebs-Standard heisst: keine Abweichung speichern.
                      if (bp === (paymentTermsData?.defaults?.depositPercentBp ?? null)) {
                        return void onSavePaymentTerms({ depositPercentBp: null, depositAmountCents: null });
                      }
                      void onSavePaymentTerms({ depositPercentBp: bp, depositAmountCents: null });
                    }}
                  />
                </label>
                ) : (
                <label className="block">
                  <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                    Anzahlung als fester Betrag (€)
                  </span>
                  <Input
                    key={`da-${ptLoadedFor ?? ""}-${anzahlungFixText}`}
                    inputMode="decimal"
                    defaultValue={anzahlungFixText}
                    placeholder="z. B. 500"
                    onBlur={(e) => {
                      const raw = e.target.value.trim();
                      if (raw === "") return void onSavePaymentTerms({ depositAmountCents: null });
                      const cents = parseGermanEurosToCents(raw);
                      if (cents == null) return setError("Ungültiger Anzahlungs-Fixbetrag.");
                      void onSavePaymentTerms({ depositAmountCents: cents, depositPercentBp: null });
                    }}
                  />
                </label>
                )}
              </div>
              <div className="grid gap-3.5 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                    Wie wird die Anzahlung bezahlt?
                  </span>
                  <Select
                    ariaLabel="Wie wird die Anzahlung bezahlt?"
                    disabled={!anzahlungAn}
                    value={anzahlungsWegWert(ptOverride, ptOverride.paymentPathway ?? paymentTermsData?.effective?.paymentPathway)}
                    onValueChange={(v) => void onSavePaymentTerms(anzahlungsWegFelder(v))}
                    /* Punkt 45: „Keine" ist hier VERSCHWUNDEN — es war nie ein Weg, sondern das
                       Gegenteil eines Weges, und genau daraus entstand der Ausfall vom 17.09.2026.
                       Darüber entscheidet jetzt der Schalter. Wege, die dieser Betrieb nicht hat,
                       stehen mit dem Grund dabei, statt angeboten zu werden und später zu scheitern. */
                    /* Kein Eintrag „wie Standard" mehr: der Standard ist einfach eingetragen
                       (Antoine, 18.09.2026 — „Standard muss kein eigenes Feld sein"). */
                    /*
                      Punkt 57 (Antoine 18.09.2026, auf die Frage, was der Gast ohne Signaturzugang
                      sieht): „Dann darf der Betreiber gar nicht Kostenübernahme auswählen können."

                      Die Kostenübernahme IST der Vertrag — ohne Signaturzugang entsteht keiner.
                      Sie wegzulassen ist deshalb etwas anderes als bei Stripe oder der
                      Überweisung: dort führt „nicht eingerichtet" zu einer Zahlung, die auf einem
                      anderen Weg trotzdem ankommt. Hier gäbe es überhaupt kein Ergebnis.

                      `verfuegbar` ist bei noch nicht geladenen Wegen `true` (siehe
                      `anzahlungsWege`) — es wird also nichts vorschnell ausgeblendet.
                    */
                    options={anzahlungsWege(zahlungswege)
                      .filter((w) => w.value !== "kostenuebernahme" || w.verfuegbar)
                      .map((w) => ({
                        value: w.value,
                        label: w.verfuegbar ? w.label : `${w.label} — nicht eingerichtet`,
                      }))}
                  />
                </label>
                <div className="block">
                  <label htmlFor={`ddd-${ptLoadedFor ?? ""}`} className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                    Anzahlung fällig
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      key={`ddd-${ptLoadedFor ?? ""}`}
                      id={`ddd-${ptLoadedFor ?? ""}`}
                      className="w-20"
                      inputMode="numeric"
                      defaultValue={ptOverride.depositDueDays ?? paymentTermsData?.effective?.depositDueDays ?? ""}
                      placeholder="7"
                      onBlur={(e) => {
                        const raw = e.target.value.trim();
                        if (raw === "") return void onSavePaymentTerms({ depositDueDays: null });
                        const n = Number(raw.replace(",", "."));
                        if (!Number.isInteger(n) || n < 0) return setError("Ungültige Anzahlungs-Frist – bitte volle Tage angeben.");
                        void onSavePaymentTerms({ depositDueDays: n === (paymentTermsData?.defaults?.depositDueDays ?? null) ? null : n });
                      }}
                    />
                    <span className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">Tage</span>
                  </div>
                  <div className="mt-2">
                    <Segmente
                      label="Bezugspunkt der Anzahlungs-Frist"
                      werte={ANZAHLUNG_FRIST_MODI}
                      wert={(ptOverride.depositDueMode ?? paymentTermsData?.effective?.depositDueMode ?? "nach_bestaetigung") as AnzahlungFristModus}
                      onWahl={(k) =>
                        void onSavePaymentTerms({
                          depositDueMode: k === (paymentTermsData?.defaults?.depositDueMode ?? "nach_bestaetigung") ? null : k,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
              {/* WARTESCHLANGE Punkt 45: deckt die Anzahlung bereits alles, bleibt nichts uebrig —
                  dann gehoert dieser Block nicht ausgegraut, sondern WEG. Ein Feld, das nichts
                  bewirkt, ist eine Falle: man fuellt es aus und glaubt, etwas geregelt zu haben.
                  Ausnahme Kostenuebernahme (K2) — die ist ein Pfand, kein Geld. */}
              {restEntfaellt ? (
                <p className="rounded-lg bg-surface-variant/40 px-3 py-2.5 font-body-sm text-body-sm text-on-surface-variant dark:bg-surface-dim/40 dark:text-secondary-fixed-dim">
                  Die Anzahlung deckt den vollen Betrag – es bleibt keine Restzahlung offen.
                </p>
              ) : (
              <div className="grid gap-3.5 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                    Wie wird die Restzahlung bezahlt?
                  </span>
                  {/* WARTESCHLANGE Punkt 45: hier stand „Restzahlungsart" mit vier Werten, die WEG
                      UND FRIST vermischten — „Rechnung vor Event" sagte beides zugleich. Wer die
                      Frist ändern wollte, musste den Weg anfassen. Jetzt zwei Felder; die
                      Übersetzung in den Altbestand steht in `restzahlungFelder`/`restzahlungAus`. */}
                  <Select
                    ariaLabel="Wie wird die Restzahlung bezahlt?"
                    value={rest.weg}
                    onValueChange={(v) => void onSavePaymentTerms(restzahlungFelder(v, fristArt, rest.tage))}
                    options={restzahlungsWege(zahlungswege).map((w) => ({
                      value: w.value,
                      label: w.verfuegbar ? w.label : `${w.label} — nicht eingerichtet`,
                    }))}
                  />
                </label>
                <label className="block">
                  <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                    Wann ist die Restzahlung fällig?
                  </span>
                  {/*
                    Antoine, 18.09.2026: „Mach doch die Anzahl Tage in klein vor dem Feld … dann
                    kommt das nächste Feld. Das Tage Feld braucht keine Überschrift."

                    Die Angabe liest sich jetzt als EIN Satz — „5 Tage vor der Veranstaltung" —
                    statt als zwei Felder, von denen das zweite unter dem ersten steht und eine
                    eigene Frage stellt. Bei „Am Veranstaltungstag" verschwindet das Zahlenfeld
                    ganz: es gibt dann keine Tage zu zählen.
                  */}
                  <div className="flex items-center gap-2">
                    {fristArt !== "am_event_tag" && (
                      <>
                        <Input
                          key={`fdd-${ptLoadedFor ?? ""}-${fristArt}`}
                          aria-label={fristArt === "nach_event" ? "Tage nach der Veranstaltung" : "Tage vor der Veranstaltung"}
                          inputMode="numeric"
                          className="w-16 shrink-0 text-center"
                          defaultValue={rest.tage ?? ""}
                          placeholder="5"
                          onBlur={(e) => {
                            const raw = e.target.value.trim();
                            if (raw === "") return void onSavePaymentTerms(restzahlungFelder(rest.weg, fristArt, null));
                            const n = Number(raw.replace(",", "."));
                            if (!Number.isInteger(n) || n < 0) return setError("Ungültige Frist – bitte volle Tage angeben.");
                            /*
                              Antoine hat auf Rückfrage Variante (a) gewählt: „0 = Sofort" soll sich
                              je nach Auswahl selbst erklären. VOR der Veranstaltung sind null Tage
                              rechnerisch derselbe Tag — und dafür gibt es die dritte Auswahl schon.
                              Statt zwei Wege zur selben Aussage stehenzulassen (genau der
                              Widerspruch, den Antoine an der alten Maske bemängelt hat), springt
                              die Auswahl hier selbst um. NACH der Veranstaltung bleibt 0 stehen und
                              bedeutet „sofort danach fällig" — dort ist es eine eigene Aussage.
                            */
                            if (n === 0 && fristArt === "vor_event") {
                              setFristWahl("am_event_tag");
                              return void onSavePaymentTerms(restzahlungFelder(rest.weg, "am_event_tag", null));
                            }
                            void onSavePaymentTerms(restzahlungFelder(rest.weg, fristArt, n));
                          }}
                        />
                        <span className="shrink-0 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                          Tage
                        </span>
                      </>
                    )}
                    <div className="min-w-0 flex-1">
                  <Select
                    ariaLabel="Wann ist die Restzahlung fällig?"
                    value={fristArt}
                    onValueChange={(v) => {
                      const art = v as RestFristArt;
                      setFristWahl(art);
                      // „Am Veranstaltungstag" ist sofort vollständig und wird gespeichert. Die
                      // beiden Tages-Formen brauchen erst eine Zahl — bis dahin steht nur die
                      // Auswahl, damit das Feld dafür überhaupt erscheint.
                      if (art === "am_event_tag") void onSavePaymentTerms(restzahlungFelder(rest.weg, art, null));
                      else if (rest.tage != null) void onSavePaymentTerms(restzahlungFelder(rest.weg, art, rest.tage));
                    }}
                    /* „Nach der Veranstaltung" kennt der Altbestand nur mit Überweisung
                       (`invoice_after_event`). Ehrlich eingeschränkt statt frei erfunden — eine
                       Kodierung, die das Feld nicht hergibt, wäre die nächste Doppelbedeutung. */
                    /* Die Wörter „Tage" stehen jetzt links im Satz, nicht mehr in der Auswahl —
                       sonst stünde dort „5 Tage Tage vor der Veranstaltung". */
                    options={[
                      { value: "vor_event", label: "vor der Veranstaltung" },
                      { value: "am_event_tag", label: "Am Veranstaltungstag" },
                      ...(rest.weg === "ueberweisung" ? [{ value: "nach_event", label: "nach der Veranstaltung" }] : []),
                    ]}
                  />
                    </div>
                  </div>
                  {fristArt === "nach_event" && (
                    <p className="mt-1 font-label-md text-label-md text-on-surface-variant dark:text-secondary-fixed-dim">
                      0 = sofort nach der Veranstaltung fällig
                    </p>
                  )}
                </label>
              </div>
              )}
              {/* § 8 (Migration 345, 31.08.2026): Annahme-/Zahlungsfluss-Umschalter. */}
              <div className="grid gap-3.5 sm:grid-cols-2">
                <label className="block">
                  {/* Antoine, 18.09.2026: „Annahme-Weg des Gasts" — „dafür anderes Wort finden,
                      ist zu umständlich formuliert". Die Frage sagt jetzt schlicht, was sie
                      entscheidet, und die Antworten sind ganze Sätze statt Fachwörter. */}
                  <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                    Wenn der Gast zusagen möchte
                  </span>
                  <Select
                    ariaLabel="Wenn der Gast zusagen möchte"
                    value={ptOverride.acceptanceFlow ?? paymentTermsData?.effective?.acceptanceFlow ?? "explicit"}
                    onValueChange={(v) => void onSavePaymentTerms({ acceptanceFlow: v as PaymentTermsFields["acceptanceFlow"] })}
                    options={[
                      { value: "explicit", label: "Erst annehmen, dann bezahlen" },
                      { value: "direct_payment", label: "Direkt zur Zahlung" },
                    ]}
                  />
                </label>
                {/* WARTESCHLANGE Punkt 45: „Zahlungsweg nach der Annahme" ist HIER verschwunden.
                    Er trug genau einen Fall — die Kostenuebernahme —, und die steht jetzt dort, wo
                    sie hingehoert: bei den Wegen der Anzahlung. Zwei Felder fuer eine Entscheidung
                    sind zwei Stellen, an denen sie sich widersprechen koennen. */}
              </div>
              <label className="block">
                <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">Angebots-Gültigkeit</span>
                <DateInput
                  className="max-w-[220px]" inputClassName="!py-2"
                  defaultValue={offerValidUntil ?? ""}
                  key={`ovu-${offerValidUntil ?? ""}`}
                  onCommit={(v) => void onPatchValidUntil(v)}
                />
              </label>
              <div className="block">
                <span className="mb-1 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
                  Stornobedingungen
                </span>
                {/* Antoine, 18.09.2026: „Der Standard muss ausgefüllt sein. Jetzt ist es
                    ausgegraut." Der Standardtext stand als Platzhalter da — grau, nicht
                    markierbar, nicht bearbeitbar. Wer ihn anpassen wollte, musste ihn abtippen.
                    Jetzt steht er als echter Text im Feld.

                    Gespeichert wird er trotzdem NUR, wenn er sich vom Standard unterscheidet:
                    sonst trüge jedes Angebot eine eigene Kopie, und eine spätere Änderung am
                    Betriebs-Standard erreichte die laufenden Angebote nicht mehr. Wer den Text
                    zurück auf den Standard bringt (oder das Feld leert), löst die Abweichung
                    wieder auf. */}
                <StornoBedingungenFeld
                  key={`ct-${ptLoadedFor ?? ""}`}
                  vorlagen={stornoVorlagenListe(paymentTermsData?.defaults)}
                  startText={ptOverride.cancellationTerms ?? paymentTermsData?.defaults?.cancellationTerms ?? ""}
                  onSpeichern={(text) => {
                    const raw = text.trim();
                    const standard = (paymentTermsData?.defaults?.cancellationTerms ?? "").trim();
                    void onSavePaymentTerms({ cancellationTerms: raw === "" || raw === standard ? null : raw });
                  }}
                />
              </div>
              {paymentTermsData?.summary && (
                <p className="rounded-lg border border-outline-variant/40 bg-surface-container-lowest p-3 font-body-sm text-body-sm text-on-surface dark:border-outline/30 dark:text-inverse-on-surface">
                  <span className="font-semibold">Zusammenfassung: </span>
                  {paymentTermsData.summary}
                </p>
              )}
              <div className="rounded-[9px] border border-outline-variant/40 bg-surface-container-lowest px-[11px] py-2 dark:border-outline/30">
                <div className="flex items-center gap-2">
                  <Icon name="visibility" className="text-[14px] shrink-0 text-primary" />
                  <span className="font-body-sm text-[11px] text-secondary-fixed-dim">
                    So sieht der Gast diese Angaben auf der Angebotsseite und in der Rechnung.
                  </span>
                  <Link
                    to={angebotsseitePfad(eventId!)}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-auto shrink-0 text-[11px] font-semibold text-primary hover:underline"
                  >
                    Kundenseite ansehen
                  </Link>
                  <KopierKnopf
                    wert={`${window.location.origin}${angebotsseitePfad(eventId!)}`}
                    titel="Adresse der Kundenseite kopieren"
                  />
                </div>
                {/*
                  Punkt 58: der Weg zur echten Stripe-Zahlungsseite, damit der Betrieb sie selbst
                  anschauen kann.

                  Gezeigt wird er, sobald eine offene Zahlung mit hinterlegtem Link existiert — und
                  NUR dann. Ein Knopf, der ins Leere führt, wäre schlechter als keiner: er verspricht
                  eine Seite, die es noch nicht gibt.

                  Antoine möchte den Link „von Anfang an", also schon vor der Annahme. Das ist der
                  zweite Schritt und berührt echtes Geld — eine vorab erzeugte Zahlung darf beim
                  späteren Annehmen keine zweite daneben entstehen lassen. Er ist als eigener Punkt
                  in `docs/WARTESCHLANGE.md` festgehalten, damit er nicht als erledigt gilt.
                */}
                {zahlungsLink && (
                  <div className="mt-1.5 flex items-center gap-2 border-t border-outline-variant/30 pt-1.5 dark:border-outline/20">
                    <Icon name="credit_card" className="text-[14px] shrink-0 text-primary" />
                    <span className="font-body-sm text-[11px] text-secondary-fixed-dim">
                      Die Zahlungsseite, die der Gast nach dem Annehmen öffnet.
                    </span>
                    <a
                      href={zahlungsLink}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-auto shrink-0 text-[11px] font-semibold text-primary hover:underline"
                    >
                      Stripe-Zahlung ansehen
                    </a>
                    <KopierKnopf wert={zahlungsLink} titel="Adresse der Zahlungsseite kopieren" />
                  </div>
                )}
              </div>
              {ptSaving && <p className="font-body-sm text-[11px] text-secondary-fixed-dim">Speichert …</p>}
              {ptError && <ErrorNote message={ptError} />}
            </>
          )}
        </Card>
      )}

      {/* Schnellstart (8a): legt eine NEUE Variante an und befuellt sie additiv aus
          allen Sektionen des Pakets (Punkt 2, 20.08.2026) - die aktive Variante bleibt
          unveraendert, siehe applyPackage() in builder.tsx. */}
      {offenerReiter === "pakete" && editable && (
        <PaketUebernehmenPanel aktivePakete={aktivePakete} loading={packagesLoading} onApply={onApplyPackage} />
      )}
    </>
  );
}
